import { getImageUrl } from "../admin/api/masterApi";

const API_BASE_URL = (
  process.env.REACT_APP_API_URL || "https://api.trivomtara.com/api"
).replace(/\/$/, "");

function isActive(status) {
  if (status === undefined || status === null) return true;
  if (typeof status === "boolean") return status;
  if (typeof status === "string") {
    const normalized = status.trim().toLowerCase();
    return (
      normalized === "active" ||
      normalized === "yes" ||
      normalized === "1" ||
      normalized === "true"
    );
  }
  return Number(status) === 1;
}

function isServiceProvider(row) {
  const role = `${row.user_role_code || ""} ${row.user_role || ""}`
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return role === "service_provider" || role === "service_providers";
}

function parseIdList(value) {
  if (Array.isArray(value)) {
    return value.map(String).filter(Boolean);
  }
  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean);
    } catch (_error) {
      return value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
  }
  return [];
}

function stripTags(html) {
  if (!html) return "";
  return String(html)
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function formatExperience(years) {
  if (years == null || years === "") return "";
  const numeric = Number(years);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return String(years);
  }
  const whole = Math.floor(numeric);
  return `${whole}+ Year Of Experience`;
}

async function fetchJson(path, fallbackMessage) {
  const response = await fetch(`${API_BASE_URL}${path}`);
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.message || fallbackMessage);
  }
  return data.data || [];
}

export function mapAstrologer(
  row,
  specializationMap = new Map(),
  serviceMap = new Map()
) {
  const name = [row.user_title, row.first_name, row.last_name]
    .filter(Boolean)
    .join(" ");
  const specializationIds = parseIdList(row.specialization_ids);
  const specializationNames = specializationIds
    .map((id) => specializationMap.get(String(id)))
    .filter(Boolean);

  const linkedAssetIds = parseIdList(row.linked_asset_ids);
  const linkedServices = linkedAssetIds
    .map((id) => serviceMap.get(String(id)))
    .filter(Boolean);

  const linkedSubCategories = [
    ...new Set(
      linkedServices
        .map((service) => service.service_sub_category_name)
        .filter(Boolean)
    ),
  ];

  // Categories drive specialization-tab filtering (user_master.specialization_ids).
  const categories = specializationNames;
  const specialty =
    specializationNames[0] || linkedSubCategories[0] || "Astrologer";

  const language =
    row.languages ||
    row.language ||
    (Array.isArray(row.spoken_languages)
      ? row.spoken_languages.filter(Boolean).join(", ")
      : "") ||
    "";

  const priceRaw = row.price_per_min ?? row.pricePerMin ?? row.consultation_fee;
  const pricePerMin =
    priceRaw == null || priceRaw === "" ? NaN : Number(priceRaw);

  return {
    id: String(row.user_id),
    name: name || "Astrologer",
    specialty,
    categories,
    specializationIds,
    language,
    experience: formatExperience(row.experience_years),
    experienceRaw: row.experience_years || "",
    pricePerMin: Number.isFinite(pricePerMin) && pricePerMin > 0 ? pricePerMin : null,
    ctaLabel: row.cta_label || "Book a Consultation",
    image: getImageUrl(row.profile_picture),
    bio: stripTags(row.brief_bio),
  };
}

/**
 * Same source as the mobile app: active service-provider users.
 * Tabs come from `/specializations` (and predictive sub-categories as fallback).
 */
export async function fetchAstrologers() {
  const [users, specializations, services, subCategories] = await Promise.all([
    fetchJson("/users", "Failed to fetch astrologers"),
    fetchJson("/specializations", "Failed to fetch specializations").catch(
      () => []
    ),
    fetchJson("/services", "Failed to fetch services").catch(() => []),
    fetchJson("/service-sub-categories", "Failed to fetch sub-categories").catch(
      () => []
    ),
  ]);

  const specializationMap = new Map(
    specializations.map((item) => [
      String(item.specialization_id),
      item.specialization_name,
    ])
  );
  const serviceMap = new Map(
    services.map((item) => [String(item.service_id), item])
  );

  const astrologers = users
    .filter((row) => isActive(row.status) && isServiceProvider(row))
    .map((row) => mapAstrologer(row, specializationMap, serviceMap));

  const specializationTabs = specializations
    .filter((item) => isActive(item.status))
    .map((item) => item.specialization_name)
    .filter(Boolean);

  // All active specializations from master API (fallback: predictive sub-categories).
  let tabs = specializationTabs;
  if (!tabs.length) {
    tabs = subCategories
      .filter((item) => {
        if (!isActive(item.status)) return false;
        const category = `${item.service_category_name || ""}`.toLowerCase();
        return category.includes("predictive");
      })
      .map((item) => item.service_sub_category_name)
      .filter(Boolean);
  }

  tabs = [...new Set(tabs)];

  return { astrologers, tabs };
}

/**
 * Single astrologer / service-provider by user id (same source as mobile).
 */
export async function fetchAstrologerById(astrologerId) {
  const id = String(astrologerId || "").trim();
  if (!id) throw new Error("Astrologer not found");

  const [userRes, specializations, services] = await Promise.all([
    fetch(`${API_BASE_URL}/users/${encodeURIComponent(id)}`).then(
      async (response) => {
        const data = await response.json();
        if (!response.ok || !data.success) {
          throw new Error(data.message || "Astrologer not found");
        }
        return data.data;
      }
    ),
    fetchJson("/specializations", "Failed to fetch specializations").catch(
      () => []
    ),
    fetchJson("/services", "Failed to fetch services").catch(() => []),
  ]);

  const row = userRes;
  if (!row || !isActive(row.status) || !isServiceProvider(row)) {
    throw new Error("Astrologer not found");
  }

  const specializationMap = new Map(
    specializations.map((item) => [
      String(item.specialization_id),
      item.specialization_name,
    ])
  );
  const serviceMap = new Map(
    services.map((item) => [String(item.service_id), item])
  );

  return mapAstrologer(row, specializationMap, serviceMap);
}

/**
 * Filter by selected specialization tab.
 * Providers with no specializations still appear (most user-master rows).
 * Providers tagged with other specializations only are hidden.
 */
export function filterAstrologersByTab(astrologers, activeCategory) {
  if (!activeCategory) return astrologers;
  return astrologers.filter((item) => {
    const categories = item.categories || [];
    if (!categories.length) return true;
    return categories.includes(activeCategory);
  });
}

export function formatAstrologerPrice(value) {
  if (value == null || value === "") return "-";
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return "-";
  return num.toLocaleString("en-IN");
}
